/**
 * @fileoverview Input/output schemas for the b-storyline-extractor-v1 agent.
 *
 * This agent extracts B-Stories (emotional/relational subplots) using the
 * A-Story timeline as a reference anchor. It receives the A-Story output
 * as context so the LLM can map B-Story beats against the established
 * main narrative spine without holding it in working memory.
 *
 * Output schema is flat (3 levels max) for Gemini API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { StructurePhaseSchema } from '@/domains/story-planning/schemas/structure-phases';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  synopsisInput: z.string().describe('The condensed synopsis or full text to analyze.'),
  isFullText: z.boolean().optional().describe('When true, the synopsisInput contains the full story text rather than a condensed synopsis.'),
  aStoryContext: z.string().optional().describe(
    'JSON string of the A-Story extraction output (storyline name, summary, beats). ' +
    'Provides the main narrative timeline as a reference anchor for mapping B-Story beats.'
  ),
  storyType: z.string().optional().describe('The story type (e.g., "Novelette", "Novel", "Epic"). Used to calibrate storyline beat density.'),
  projectedWordCount: z.number().optional().describe('The target word count for the story. Used to calibrate storyline beat density.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA (Flat structure for Gemini compatibility)
// =============================================================================

/**
 * A B-Story: an emotional/relational subplot.
 */
const StorylineOutputSchema = z.object({
  name: z.string().describe(
    'Short, evocative name for the B-Story (2-4 words). ' +
    'Named after the central relationship or internal struggle ("Indy & Marion", "Luke\'s Training", "Father & Son").'
  ),
  summary: z.string().describe(
    'A brief description of the B-Story (1-3 sentences). ' +
    'Captures the emotional arc: how the relationship or internal struggle begins, develops, and resolves.'
  ),
  priority: z.literal('B').describe(
    'Always "B" — this agent only extracts emotional/relational subplots.'
  ),
});

/**
 * Beat with storylineIndex reference.
 * Multiple beats per structural phase for thorough coverage, scaled to story type.
 */
const BeatOutputSchema = z.object({
  storylineIndex: z.number().describe('Index of the B-Story this beat belongs to (0-based, referencing the storylines array).'),
  storylinePhase: StructurePhaseSchema.describe(
    'The structural function this beat serves for THIS B-STORY\'s internal arc. ' +
    'A B-Story\'s Inciting Incident might occur at the global Midpoint if the emotional subplot starts late.'
  ),
  globalPhase: StructurePhaseSchema.describe(
    'Where this beat falls in the OVERALL story timeline, anchored by the A-Story. ' +
    'B-Stories often start later than the A-Story, so globalPhase may differ from storylinePhase ' +
    '(e.g. a B-Story\'s "Initial State" at the global "Break Into Two").'
  ),
  plotEvent: z.string().describe(
    'The concrete, observable event in this emotional/relational subplot. ' +
    'Focus on relationship moments—conversations, revelations, betrayals, reconciliations, sacrifices.'
  ),
  narrativeState: z.string().optional().describe(
    'The emotional state of this relationship or internal struggle at this moment.'
  ),
  goalProgress: z.number().min(-1).max(1).describe(
    'Progress toward the B-Story\'s emotional resolution. ' +
    '+1 = Major emotional advancement. -1 = Significant emotional setback. 0 = Neutral.'
  ),
});

/**
 * Story question for a B-Story.
 */
const StoryQuestionOutputSchema = z.object({
  storylineIndex: z.number().describe('Index of the B-Story this question belongs to (0-based).'),
  question: z.string().describe('The dramatic question about this relationship or emotional arc (e.g., "Will Marion forgive Indy?")'),
  status: z.enum(['open', 'answered']).default('open').describe('Whether this question is still open or has been answered in the text'),
});

/**
 * Output schema for the b-storyline-extractor-v1 agent.
 *
 * Flat structure (3 levels max) for Gemini API compatibility.
 * Contains one or more B-Stories with thorough beat coverage.
 */
export const OutputSchema = z.object({
  storylines: z.array(StorylineOutputSchema).describe(
    'B-Stories extracted from the text. These are the emotional/relational subplots.'
  ),
  beats: z.array(BeatOutputSchema).describe(
    'All beats for B-Stories across 7 structural phases. ' +
    'Beat count per phase should match the story type — see prompt guidance.'
  ),
  storyQuestions: z.array(StoryQuestionOutputSchema).describe(
    'Dramatic questions for the B-Stories. These are the emotional hooks.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;

// Type exports for consumer convenience
export type StorylineOutput = z.infer<typeof StorylineOutputSchema>;
export type BeatOutput = z.infer<typeof BeatOutputSchema>;
export type StoryQuestionOutput = z.infer<typeof StoryQuestionOutputSchema>;
